import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fullName, upcomingBirthdays } from "../../lib/format";
import { CustomIcon } from "../shared/CustomIcon";
import { Avatar } from "../shared/Avatar";
import { API_BASE as SHARED_API_BASE, authHeaders } from "../../lib/apiClient";

// =============================================================
// BirthdaysThisWeekPage — full-page view showing every member
// with a birthday inside the current calendar week (Monday to
// Sunday, in the browser's local timezone).
// =============================================================

const API_BASE = SHARED_API_BASE;

// Monday-start week bounds (inclusive from, exclusive to).
function currentWeekBounds() {
  const now = new Date();
  const day = now.getDay();               // 0=Sun … 6=Sat
  const daysSinceMonday = (day + 6) % 7;  // Mon→0, Tue→1, ... Sun→6
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysSinceMonday);
  const end   = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7);
  return { start, end };
}

function formatWeekLabel(start, end) {
  const opts = { month: "long", day: "numeric" };
  return `${start.toLocaleDateString(undefined, opts)} – ${new Date(end.getTime() - 86400000).toLocaleDateString(undefined, opts)}`;
}

export default function BirthdaysThisWeekPage() {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Iter 82 bug fix: was using the wrong localStorage key ("kcToken")
        // so every request went unauthenticated and returned 401 → members=[]
        // → page perpetually empty. Delegate to the shared auth helper.
        const r = await fetch(`${API_BASE}/members`, { headers: { ...authHeaders() } });
        if (!r.ok) throw new Error("Could not load members");
        const j = await r.json();
        setMembers(Array.isArray(j) ? j : (j?.members || j?.items || []));
      } catch { setMembers([]); }
      setLoading(false);
    })();
  }, []);

  const { start, end } = useMemo(currentWeekBounds, []);

  // Everyone with a birthday whose (month, day) falls inside the [start, end)
  // week — ignoring year, using local timezone. Iter 82 bug fix: previous
  // logic reconstructed `thisYear = new Date(start.getFullYear(), mm-1, dd)`
  // which mis-fires across December→January week boundaries. We now walk each
  // day in the week and match on month+day, which is timezone-safe and never
  // off-by-one.
  const weekBirthdays = useMemo(() => {
    // Build a set of "M-D" strings for every day in the week window.
    const daysInWindow = new Map(); // key: "M-D" -> Date object for that day
    for (let d = new Date(start); d < end; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
      daysInWindow.set(`${d.getMonth()}-${d.getDate()}`, new Date(d));
    }
    const out = [];
    for (const m of (members || [])) {
      if (!m.dob) continue;
      const parts = String(m.dob).split("-").map(Number);
      if (parts.length < 3) continue;
      const mm = parts[1], dd = parts[2];
      if (!mm || !dd) continue;
      const key = `${mm - 1}-${dd}`;
      const dayInWeek = daysInWindow.get(key);
      if (dayInWeek) out.push({ ...m, thisYearBirthday: dayInWeek });
    }
    out.sort((a, b) => a.thisYearBirthday - b.thisYearBirthday);
    return out;
  }, [members, start, end]);

  // If nobody this week — pick the next single upcoming birthday.
  const nextUp = useMemo(() => {
    if (weekBirthdays.length > 0) return null;
    return upcomingBirthdays(members, 365).slice(0, 1)[0] || null;
  }, [members, weekBirthdays]);

  return (
    <div data-testid="birthdays-week-page" style={{ background: "var(--kc-bg)", minHeight: "100vh", color: "var(--kc-text)", padding: "20px clamp(16px,4vw,32px)" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <button
          onClick={() => navigate(-1)}
          data-testid="birthdays-back"
          style={{ background: "transparent", border: "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)", color: "var(--kc-accent)", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, marginBottom: 16, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          ← Back
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <CustomIcon name="birthday-cake" size={26} />
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "var(--kc-accent)", letterSpacing: "0.10em", textTransform: "uppercase" }}>
            Birthdays This Week
          </div>
        </div>
        <div style={{ fontSize: 12, opacity: 0.65, marginBottom: 18 }}>
          {formatWeekLabel(start, end)} · Monday to Sunday
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: "center", opacity: 0.6 }} data-testid="birthdays-week-loading">Loading…</div>
        ) : weekBirthdays.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="birthdays-week-list">
            {weekBirthdays.map(m => (
              <button
                key={m.id}
                type="button"
                onClick={() => navigate(`/members/${m.id}`)}
                data-testid={`birthdays-week-item-${m.id}`}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: 12, borderRadius: 12,
                  background: m.daysUntilBirthday === 0 ? "color-mix(in srgb, var(--kc-accent) 20%, transparent)" : "rgba(0,0,0,0.28)",
                  border: `1px solid ${m.daysUntilBirthday === 0 ? "var(--kc-accent)" : "color-mix(in srgb, var(--kc-accent) 20%, transparent)"}`,
                  color: "#FFF", textAlign: "left", cursor: "pointer",
                }}
              >
                <Avatar name={fullName(m)} photo={m.photo} memberId={m.id} status={m.memberStatus} style={{ width: 44, height: 44, fontSize: 16 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{fullName(m)}</div>
                  <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                    {m.thisYearBirthday.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                  </div>
                </div>
                {m.daysUntilBirthday === 0 ? (
                  <span data-testid={`birthdays-week-today-${m.id}`} style={{ background: "var(--kc-accent)", color: "#1a1a1a", padding: "4px 10px", borderRadius: 999, fontWeight: 800, fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <CustomIcon name="party" size={12} /> Today
                  </span>
                ) : (
                  <span style={{ fontSize: 12, opacity: 0.75 }}>in {m.daysUntilBirthday} day{m.daysUntilBirthday === 1 ? "" : "s"}</span>
                )}
              </button>
            ))}
          </div>
        ) : nextUp ? (
          <div data-testid="birthdays-week-empty-nextup" style={{
            padding: 20, borderRadius: 12,
            background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)",
            textAlign: "center",
          }}>
            <div style={{ fontSize: 12, opacity: 0.7, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700, marginBottom: 8 }}>
              No birthdays this week
            </div>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: 17, fontWeight: 700, color: "var(--kc-accent)" }}>
              Next up: {fullName(nextUp)}
            </div>
            <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>
              {nextUp.daysUntilBirthday} day{nextUp.daysUntilBirthday === 1 ? "" : "s"} away
            </div>
            <button
              type="button"
              onClick={() => navigate(`/members/${nextUp.id}`)}
              style={{ marginTop: 12, padding: "6px 14px", background: "var(--kc-accent)", color: "#111", fontWeight: 700, border: "none", borderRadius: 8, cursor: "pointer" }}
              data-testid="birthdays-week-nextup-view"
            >View profile</button>
          </div>
        ) : (
          <div style={{ padding: 24, textAlign: "center", opacity: 0.6 }} data-testid="birthdays-week-empty">
            No birthdays on file.
          </div>
        )}
      </div>
    </div>
  );
}
